import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';
import { 
getPaymentMode, 
} from '../../utils/helpers';

// Helper function to get status name
const getStatusName = (status) => {
return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to format currency
const formatCurrency = (amount) => {
return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
}).format(parseFloat(amount) || 0) + " "+"sh";
};

// Helper function to get customer name
const getCustomerName = (customerId, customerData) => {
const customer = customerData.find(c => parseInt(c.id) === parseInt(customerId));
return customer ? customer.customer_name : 'Unknown';
};

// Helper function to get branch name
const getBranchName = (branchId, branchesData) => {
const branch = branchesData.find(b => parseInt(b.id) === parseInt(branchId));
return branch ? `${branch.name}` : 'Unknown';
};

// Excel export for Address System Bookings (with all attributes)
export const exportToExcel = (bookings, branchesData = [],  customerData = [], filters = {}) => {
try {
    // Prepare data for Excel with all attributes
    const excelData = bookings.map((booking, index) => ({
    'No.': index + 1,
    'Booking Number': booking.booking_no || '',
    'Date': new Date(booking.created_at).toLocaleDateString(),
    'Customer Name': getCustomerName(booking.customer_id, customerData),
    'Customer Phone': booking.customer_phone || '',
    'Customer Email': booking.customer_email || '',
    'Customer Address': booking.customer_address || '',
    'Place Name': booking.place_name || '',
    'Additional Info': booking.additional_info || '',
    'Cost per Unit': formatCurrency(booking.cost),
    'Quantity': booking.quantity,
    'Total Amount': formatCurrency(booking.total_amount),
    'Paid Amount': formatCurrency(booking.paid_amount),
    'Balance Amount': formatCurrency(booking.balance_amount),
    'Payment Method': getPaymentMode(booking.payment_method),
    'Branch': getBranchName(booking.branch_id, branchesData),
    'Status': getStatusName(booking.status),
    'Description': booking.description || '',
    'Check-in Date': booking.check_in_date || '',
    'Check-out Date': booking.check_out_date || '',
    'Created Date': new Date(booking.created_at).toLocaleDateString(),
    'Updated Date': new Date(booking.updated_at).toLocaleDateString()
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
    Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
        widths[idx] = length;
        }
    });
    return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Address System Bookings');

    // Generate Excel file and download
    const fileName = `address_system_bookings_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Address System Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// PDF export for Address System Bookings - Landscape mode (without autotable)
export const exportToPDF = (bookings, branchesData = [],  customerData = [], filters = {}) => {
try {
    // Create PDF in landscape mode
    const doc = new jsPDF('landscape');
    
    // Set document properties
    doc.setProperties({
    title: 'Address System Bookings Report',
    subject: 'Address System Bookings Export',
    author: companyInfo.name,
    keywords: 'address system, bookings, export, report',
    creator: companyInfo.name
    });
    
    // Page dimensions
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 14;
    const tableStartY = 60;
    const rowHeight = 12;
    const headerHeight = 15;
    
    // Column widths (10 columns)
    const colWidths = [
    15,  // # (15mm)
    45,  // Number (30mm)
    25,  // Date (25mm)
    30,  // Customer (50mm)
    25,  // Cost (25mm)
    20,  // Qty (20mm)
    30,  // Total (30mm)
    30,  // Paid (30mm)
    30,  // Balance (30mm)
    25   // Branch (40mm)
    ];
    
    // Calculate total table width
    const totalTableWidth = colWidths.reduce((sum, width) => sum + width, 0);
    
    // Check if table fits within page width (with margins)
    const availableWidth = pageWidth - (2 * margin);
    if (totalTableWidth > availableWidth) {
    // Scale down proportionally if too wide
    const scaleFactor = availableWidth / totalTableWidth;
    for (let i = 0; i < colWidths.length; i++) {
        colWidths[i] = Math.floor(colWidths[i] * scaleFactor);
    }
    }
    
    // Recalculate total width after scaling
    const finalTableWidth = colWidths.reduce((sum, width) => sum + width, 0);
    
    // Add title and header
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('ADDRESS SYSTEM BOOKINGS REPORT', pageWidth / 2, 25, { align: 'center' });
    
    // Company info
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(companyInfo.name, margin, 35);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - margin, 35, { align: 'right' });
    
    // Report info
    doc.text(`Total Records: ${bookings.length}`, margin, 45);
    
    // Filters info - simplified
    let filtersY = 45;
    if (Object.keys(filters).some(key => filters[key])) {
    doc.setFontSize(9);
    doc.setTextColor(25, 118, 210);
    
    const filterTexts = [];
    
    if (filters.customer_id) {
        const customer = customerData.find(c => parseInt(c.id) === parseInt(filters.customer_id));
        filterTexts.push(`Customer: ${customer ? customer.customer_name : filters.customer_id}`);
    }
    
    if (filters.payment_method) {
        filterTexts.push(`Payment: ${getPaymentMode(filters.payment_method)}`);
    }
    
    if (filters.branch_id) {
        const branch = branchesData.find(b => parseInt(b.id) === parseInt(filters.branch_id));
        filterTexts.push(`Branch: ${branch ? `${branch.name}` : filters.branch_id}`);
    }
    
    if (filters.status) {
        filterTexts.push(`Status: ${getStatusName(filters.status)}`);
    }
    
    if (filters.dateFrom) {
        filterTexts.push(`From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
    }
    
    if (filters.dateTo) {
        filterTexts.push(`To: ${new Date(filters.dateTo).toLocaleDateString()}`);
    }
    
    // Display filters in a compact way
    if (filterTexts.length > 0) {
        const filtersText = filterTexts.join(' | ');
        doc.text(`Filters: ${filtersText}`, margin, filtersY + 7);
        filtersY += 15;
    }
    }
    
    // Table headers
    const headers = ['#', 'Number', 'Date', 'Customer', 'Cost', 'Qty', 'Total', 'Paid', 'Balance', 'Branch'];
    
    let currentY = Math.max(tableStartY, filtersY + 5);
    
    // Draw header background
    doc.setFillColor(25, 118, 210);
    doc.rect(margin, currentY, finalTableWidth, headerHeight, 'F');
    
    // Draw header text
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    
    let xPos = margin;
    headers.forEach((header, index) => {
    const textX = xPos + 2;
    const textY = currentY + (headerHeight / 2) + 3;
    
    // Center text in column
    const textWidth = doc.getTextWidth(header);
    const centerX = textX + (colWidths[index] / 2) - (textWidth / 2);
    
    // If center alignment doesn't work well, use left alignment with padding
    if (centerX < textX) {
        doc.text(header, textX, textY);
    } else {
        doc.text(header, centerX, textY);
    }
    xPos += colWidths[index];
    });
    
    currentY += headerHeight;
    
    // Draw table rows
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    
    bookings.forEach((booking, rowIndex) => {
    // Check for page break
    if (currentY + rowHeight > pageHeight - margin) {
        doc.addPage('landscape');
        currentY = margin;
        
        // Redraw header on new page
        doc.setFillColor(25, 118, 210);
        doc.rect(margin, currentY, finalTableWidth, headerHeight, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        
        xPos = margin;
        headers.forEach((header, index) => {
        const textX = xPos + 2;
        const textY = currentY + (headerHeight / 2) + 3;
        const textWidth = doc.getTextWidth(header);
        const centerX = textX + (colWidths[index] / 2) - (textWidth / 2);
        
        if (centerX < textX) {
            doc.text(header, textX, textY);
        } else {
            doc.text(header, centerX, textY);
        }
        xPos += colWidths[index];
        });
        
        currentY += headerHeight;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
    }
    
    // Alternate row colors for better readability (no borders needed)
    if (rowIndex % 2 === 0) {
        doc.setFillColor(248, 248, 248);
        doc.rect(margin, currentY, finalTableWidth, rowHeight, 'F');
    }
    // Odd rows will have white background (default)
    
    // Prepare row data
    const rowData = [
        (rowIndex + 1).toString(),
        booking.booking_no || '-',
        new Date(booking.created_at).toLocaleDateString('en-GB'),
        getCustomerName(booking.customer_id, customerData),
        formatCurrency(booking.cost),
        booking.quantity.toString(),
        formatCurrency(booking.total_amount),
        formatCurrency(booking.paid_amount),
        formatCurrency(booking.balance_amount),
        getBranchName(booking.branch_id, branchesData)
    ];
    
    // Draw row text
    doc.setTextColor(0, 0, 0);
    xPos = margin;
    
    rowData.forEach((cell, index) => {
        const textX = xPos + 2;
        const textY = currentY + (rowHeight / 2) + 3;
        
        // Truncate text if too long
        let displayText = String(cell);
        const maxCellWidth = colWidths[index] - 4;
        
        // Measure text width and truncate if necessary
        let textWidth = doc.getTextWidth(displayText);
        while (textWidth > maxCellWidth && displayText.length > 3) {
        displayText = displayText.substring(0, displayText.length - 4) + '...';
        textWidth = doc.getTextWidth(displayText);
        }
        
        // Right align for numeric columns (Cost, Total, Paid, Balance)
        if (index === 4 || index === 6 || index === 7 || index === 8) {
        const rightAlignX = xPos + colWidths[index] - textWidth - 2;
        doc.text(displayText, rightAlignX, textY);
        }
        // Center align for Qty
        else if (index === 5) {
        const centerX = textX + (colWidths[index] / 2) - (textWidth / 2);
        doc.text(displayText, centerX, textY);
        }
        // Left align for others
        else {
        doc.text(displayText, textX, textY);
        }
        
        xPos += colWidths[index];
    });
    
    currentY += rowHeight;
    });
    
    // Save PDF
    const fileName = `address_system_bookings_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    return { success: true, fileName };
} catch (error) {
    console.error('Address System PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
}
};

// CSV export for Address System Bookings (with all attributes)
export const exportToCSV = (bookings, branchesData = [],  customerData = []) => {
try {
    // Prepare headers with all attributes
    const headers = [
    'No.', 'Booking Number', 'Date', 'Customer Name', 'Customer Phone', 'Customer Email',
    'Customer Address', 'Place Name', 'Additional Info', 'Cost per Unit', 'Quantity',
    'Total Amount', 'Paid Amount', 'Balance Amount', 'Payment Method', 'Branch',
    'Status', 'Description', 'Check-in Date', 'Check-out Date', 'Created Date', 'Updated Date'
    ];
    
    // Prepare CSV data with all attributes
    const csvData = bookings.map((booking, index) => [
    index + 1,
    booking.booking_no || '',
    new Date(booking.created_at).toLocaleDateString(),
    `"${getCustomerName(booking.customer_id, customerData).replace(/"/g, '""')}"`,
    `"${(booking.customer_phone || '').replace(/"/g, '""')}"`,
    `"${(booking.customer_email || '').replace(/"/g, '""')}"`,
    `"${(booking.customer_address || '').replace(/"/g, '""')}"`,
    `"${(booking.place_name || '').replace(/"/g, '""')}"`,
    `"${(booking.additional_info || '').replace(/"/g, '""')}"`,
    formatCurrency(booking.cost),
    booking.quantity,
    formatCurrency(booking.total_amount),
    formatCurrency(booking.paid_amount),
    formatCurrency(booking.balance_amount),
    `"${getPaymentMode(booking.payment_method).replace(/"/g, '""')}"`,
    `"${getBranchName(booking.branch_id, branchesData).replace(/"/g, '""')}"`,
    `"${getStatusName(booking.status).replace(/"/g, '""')}"`,
    `"${(booking.description || '').replace(/"/g, '""')}"`,
    `"${(booking.check_in_date || '').replace(/"/g, '""')}"`,
    `"${(booking.check_out_date || '').replace(/"/g, '""')}"`,
    `"${new Date(booking.created_at).toLocaleDateString().replace(/"/g, '""')}"`,
    `"${new Date(booking.updated_at).toLocaleDateString().replace(/"/g, '""')}"`
    ]);
    
    // Combine headers and data
    const csvContent = [
    headers.join(','),
    ...csvData.map(row => row.join(','))
    ].join('\n');
    
    // Create and download CSV file
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `address_system_bookings_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return { success: true };
} catch (error) {
    console.error('Address System CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};